import React, { useEffect, useState } from "react";
import Select from "react-select";
import "./FormNF.scss";
import "../../../assets/styles/general/select.scss";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { Link, useNavigate } from "react-router-dom";

import { useAuthValidation } from "../../../hooks/useAuthValidation";
import api from "../../../services/api";
import apiSimulator from "../../../services/apiSimulator";
import DefaultLayout from "../../components/Layouts/DefaultLayout";

function FormNF() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading } = useAuthValidation();

  const [formData, setFormData] = useState({
    products: [{ product_id: "", value: "" }],
    number: "",
    image: "",
  });
  const [file, setFile] = useState(null);
  const [productsOptions, setProductsOptions] = useState([]);
  const [options, setOptions] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    apiSimulator
      .get("/products")
      .then((response) => {
        setProductsOptions(response.data);
      })
      .catch((error) => {
        console.error("Erro ao buscar produtos:", error);
      });
  }, []);

  useEffect(() => {
    const productOptions = productsOptions
    .filter((product) => ![33, 34, 35].includes(product.id)) 
    .map((product) => ({
      value: product.id,
      label: [
        product.brand || '', 
        product.title || ''   
      ].join(' ').trim(), 
    }));
    setOptions(productOptions);
  }, [productsOptions]);

  // Agora que todos os Hooks foram chamados, podemos fazer os returns condicionais
  if (loading) {
    return <div className="loading">{t("loading")}</div>;
  }

  if (!user) {
    return null;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevNf) => ({
      ...prevNf,
      [name]: value,
    }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: null }));
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleSelectChange = (selectedOption, field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: selectedOption ? selectedOption.value : "",
    }));
    setErrors((prevErrors) => ({ ...prevErrors, [field]: null }));
  };

  const handleProductChange = (index, field, value) => {
    const updatedProducts = [...formData.products];
    updatedProducts[index][field] = value;
    setFormData((prev) => ({ ...prev, products: updatedProducts }));
    setErrors((prevErrors) => ({ ...prevErrors, [`products.${index}.${field}`]: null }));
  };

  const addProductField = () => {
    setFormData((prev) => ({
      ...prev,
      products: [...prev.products, { product_id: "", value: "" }],
    }));
  };

  const removeProductField = (index) => {
    if (formData.products.length > 1) {
      const updatedProducts = formData.products.filter((_, i) => i !== index);
      setFormData((prev) => ({ ...prev, products: updatedProducts }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    const data = new FormData();
    data.append("number", formData.number);

    formData.products.forEach((product, index) => {
      data.append(`products[${index}][product_id]`, product.product_id);
      const numericValue = parseFloat(product.value);
      data.append(`products[${index}][value]`, !isNaN(numericValue) ? numericValue.toFixed(2) : "0.00");
    });

    if (file) {
      data.append("image", file);
    }

    try {
      await api.post("/document/create", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      navigate("/documento-cadastrado");
    } catch (error) {
      console.error("Erro:", error);
      if (error.response && error.response.data) {
        const serverErrors = error.response.data;
        const formattedErrors = Object.fromEntries(Object.entries(serverErrors).map(([key, value]) => [key, value[0]]));
        setErrors(formattedErrors);
      } else {
        setErrors({ general: t("common-submit-error") });
      }
    }
  };

  return (
    <DefaultLayout>
      <section className="form-nf">
        <div className="container">
          <h2>{t("form-nf-title")}</h2>
          <span>{t("form-nf-text")}</span>
          <form onSubmit={handleSubmit}>
            <div>
              <input type="number" name="number" placeholder={t("form-nf-input-1")} onChange={handleChange} required />
              {errors.number && <p className="error">{errors.number}</p>}
            </div>
            <div className="containerPhotoInput">
              <label>
                {file ? (
                  <p>
                    {t("form-nf-input-4")} {file.name}
                  </p>
                ) : (
                  <p>{t("form-nf-input-5")}</p>
                )}
                <input type="file" onChange={handleFileChange} className="attachment" hidden name="image" />
              </label>
              {errors.image && <p className="error">{errors.image}</p>}
            </div>
            <div className="nf-data">
              {formData.products.map((product, index) => (
                <div key={index} className="product-field">
                  <div>
                    <Select
                      options={options}
                      classNamePrefix="select"
                      placeholder={t("form-nf-select")}
                      value={options.find((opt) => opt.value === product.product_id)}
                      onChange={(option) => handleProductChange(index, "product_id", option ? option.value : "")}
                    />
                    {errors[`products.${index}.product_id`] && (
                      <p className="error">{errors[`products.${index}.product_id`]}</p>
                    )}
                  </div>
                  <div className="more-options">
                    <div>
                      <NumericFormat
                        thousandSeparator="."
                        decimalSeparator=","
                        prefix="R$ "
                        decimalScale={2}
                        fixedDecimalScale
                        placeholder={t("form-nf-input-3")}
                        allowNegative={false}
                        value={product.value}
                        onValueChange={({ value }) => handleProductChange(index, "value", value)}
                      />
                      {errors.value && <p className="error">{errors.value}</p>}
                    </div>
                    <button type="button" onClick={addProductField}>
                      +
                    </button>
                    <button type="button" onClick={() => removeProductField(index)}>
                      -
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {errors.general && <p className="error">{errors.general}</p>}
            <button type="submit">{t("form-nf-button")}</button>
          </form>
        </div>
      </section>
    </DefaultLayout>
  );
}

export default FormNF;
